import { Box, Button, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import IosShareIcon from "@mui/icons-material/IosShare";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import WifiIcon from "@mui/icons-material/Wifi";
import BatteryStdIcon from "@mui/icons-material/BatteryStd";
import RefreshIcon from "@mui/icons-material/Refresh";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { PreviewQuest, PhoneStatus } from "@quest_editor/mocks/data";
import { IncentiveType } from "@quest_editor/constants";
import { formatDateJP } from "utils/common/formatUtils";
import {
  StatusBar,
  Header,
  SectionSpacer,
  TriangleDown,
  ImageReceiptIcon,
  CoinIcon,
  BarcodeIcon,
} from "./PhoneScreen.styles";

type Props = {
  quest: PreviewQuest;
  phoneStatus?: PhoneStatus;
  showStatusBar?: boolean;
};

const PhoneScreen = ({ quest, phoneStatus, showStatusBar = true }: Props) => {
  const { t } = useTranslation("questEditor");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "white" }}>
      {showStatusBar && phoneStatus && (
        <StatusBar>
          <Typography sx={{ fontWeight: "600", fontSize: 15 }}>{phoneStatus.time}</Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <SignalCellularAltIcon sx={{ fontSize: 16 }} />
            <WifiIcon sx={{ fontSize: 16 }} />
            <Stack direction="row" alignItems="center" spacing={0.25}>
              <BatteryStdIcon sx={{ fontSize: 16, transform: "rotate(90deg)" }} />
              <Typography sx={{ fontSize: 12 }}>{phoneStatus.battery}</Typography>
            </Stack>
          </Stack>
        </StatusBar>
      )}

      <Header>
        <ChevronLeftIcon sx={{ color: "#2196f3", fontSize: 32 }} />
      </Header>

      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          "&::-webkit-scrollbar": { display: "none" },
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
      >
        <Box sx={{ position: "relative", bgcolor: "#f5f5f5", py: 3, px: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Box sx={{ position: "relative" }}>
              <Box
                component="img"
                src={quest.imageUrl}
                alt={quest.name}
                sx={{
                  width: 200,
                  height: 200,
                  objectFit: "cover",
                  borderRadius: 1,
                }}
              />
              <ImageReceiptIcon />
            </Box>
          </Box>
          <Box
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              width: 36,
              height: 36,
              borderRadius: "50%",
              bgcolor: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IosShareIcon sx={{ color: "#666", fontSize: 20 }} />
          </Box>
        </Box>

        <Box sx={{ px: 2, py: 2 }}>
          <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 0.5 }}>
            {quest.company}
          </Typography>
          <Typography sx={{ fontSize: 16, fontWeight: "bold", lineHeight: 1.4 }}>
            {quest.name}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.5 }}>
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
              {t("preview.reward")}
            </Typography>
            {quest.incentiveType === IncentiveType.TAMARU ? (
              <>
                <CoinIcon />
                <Typography sx={{ fontSize: 28, fontWeight: "bold" }}>
                  {quest.tamaruPoint}pt
                </Typography>
              </>
            ) : (
              <Typography sx={{ fontSize: 20, fontWeight: "bold" }}>
                {quest.prizeName}
              </Typography>
            )}
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
            <Typography sx={{ fontSize: 13, color: "#4caf50" }}>
              {t("preview.recruiting")}
            </Typography>
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                bgcolor: "#bdbdbd",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <RefreshIcon sx={{ fontSize: 14, color: "white" }} />
            </Box>
          </Stack>
        </Box>

        <SectionSpacer />

        <Box sx={{ px: 2, py: 2 }}>
          <Typography sx={{ fontSize: 15, fontWeight: "bold", mb: 2 }}>
            {t("preview.todo")}
          </Typography>

          <Button
            variant="contained"
            fullWidth
            startIcon={<QrCodeScannerIcon />}
            sx={{
              borderRadius: 2,
              py: 1.25,
              fontSize: 14,
              fontWeight: "bold",
              textTransform: "none",
              bgcolor: "#2196f3",
              "&:hover": { bgcolor: "#1976d2" },
            }}
          >
            {t("preview.button.scan")}
          </Button>

          <Box sx={{ display: "flex", justifyContent: "center", my: 1 }}>
            <TriangleDown />
          </Box>

          <Button
            variant="contained"
            fullWidth
            startIcon={<ReceiptLongIcon />}
            sx={{
              borderRadius: 2,
              py: 1.25,
              fontSize: 14,
              fontWeight: "bold",
              textTransform: "none",
              bgcolor: "#f44336",
              "&:hover": { bgcolor: "#d32f2f" },
            }}
          >
            {t("preview.button.receipt")}
          </Button>

          <Box sx={{ display: "flex", justifyContent: "center", my: 1 }}>
            <TriangleDown />
          </Box>

          <Button
            variant="contained"
            fullWidth
            disabled
            sx={{
              borderRadius: 2,
              py: 1.25,
              fontSize: 14,
              fontWeight: "bold",
              textTransform: "none",
              bgcolor: "#e0e0e0",
              color: "#ffffff",
              "&.Mui-disabled": {
                bgcolor: "#e0e0e0",
                color: "#ffffff",
              },
            }}
          >
            {t("preview.button.survey")}
          </Button>
        </Box>

        <SectionSpacer />

        <Box sx={{ px: 2, pt: 2 }}>
          <Typography sx={{ fontSize: 15, fontWeight: "bold", mb: 2 }}>
            {t("preview.quest_info")}
          </Typography>

          {quest.targetProducts && quest.targetProducts.length > 0 &&
            quest.targetProducts.map((product, index) => (
              <Stack
                key={index}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ pb: 1 }}
              >
                <Typography sx={{ fontSize: 13, color: "text.secondary", minWidth: 60 }}>
                  {index === 0 ? t("preview.target_product") : ""}
                </Typography>
                <Box sx={{ flex: 1, ml: 2 }}>
                  <Typography sx={{ fontSize: 14 }}>{product.name}</Typography>
                  {product.barcode && (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <BarcodeIcon />
                      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                        {product.barcode}
                      </Typography>
                    </Stack>
                  )}
                </Box>
                <ChevronRightIcon sx={{ color: "#bdbdbd" }} />
              </Stack>
            ))}

          <Stack
            direction="row"
            alignItems="center"
            sx={{ py: 1, borderTop: "1px solid #e0e0e0" }}
          >
            <Typography sx={{ fontSize: 13, color: "text.secondary", minWidth: 60 }}>
              {t("preview.period")}
            </Typography>
            <Typography sx={{ fontSize: 14, ml: 2 }}>
              {formatDateJP(quest.startAt)} {t("preview.through")} {formatDateJP(quest.endAt)}
            </Typography>
          </Stack>
        </Box>

        <SectionSpacer />

        <Box sx={{ px: 2, py: 2 }}>
          <Typography sx={{ fontSize: 15, fontWeight: "bold", mb: 2 }}>
            {t("preview.quest_description")}
          </Typography>
          <Typography sx={{ fontSize: 14, lineHeight: 1.8, whiteSpace: "pre-wrap" }}>
            {quest.description}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ height: 44, bgcolor: "white" }} />
    </Box>
  );
};

export default PhoneScreen;
